/* po-atc-guard.js — makes the cart match the builder after Add to cart.

   Traced live on 2026-09-18. Three faults, one click.

   1. Spurit's split-payment script binds a capture-phase click listener above
      the Add to cart button. Ancestors capture before the button does, so when
      Spurit is attached it beats the configurator's own listener, stops
      propagation, and posts its own add:

        id=<bed>&quantity=1&properties[_spp2-deposit]=1&selling_plan=1875509437
          at k.ajax (spurit.global-2.x.min.js:1:34031)

      Quantity is hardcoded to 1 and add-ons are not included, so the builder's
      quantity and every add-on — Rush Order Upgrade included — are lost, and the
      shopper is redirected to /cart. The ship dates then recalculate off that
      wrong cart, which is why the dates look wrong too. It only wins sometimes,
      so the fault comes and goes.

   2. The quantity field is <input type="number"> and the configurator re-reads
      it on `change`, not `input`. A typed quantity therefore leaves the summary
      and the cart on the old number until the field happens to blur.

   3. The deposit is a Shopify selling plan, not the _spp2-deposit property.
      Without the plan checkout charges in full; with it, checkout splits into
      "Due today" and "Due <date>".

   Why this repairs rather than intercepts: Spurit provisions the selling plan
   per variant at the moment its own add runs, and the plan group otherwise sits
   empty — a variant nobody has deposited on returns "Cannot apply selling plan
   to variant". Claiming the click and adding the lines ourselves would therefore
   fail outright on most colours, which is worse than the bug. Adding the
   variants up front needs write_purchase_options, which this app does not hold.

   So: let whichever path wins do the add — Spurit's add is what provisions the
   plan — then reconcile the cart against what the builder actually showed.
   Quantities are only ever raised, never lowered, so a shopper editing their own
   cart is never fought. Verified: changing quantity preserves the selling plan,
   and change.js can attach the plan to a line that is missing it. */
(function () {
  if (window.__poAtcGuard) return;
  window.__poAtcGuard = true;

  var KEY = 'po-atc-intent';
  var STALE = 'po-cart-stale';
  var MAX_AGE = 120000; // an intent older than this is stale; ignore it
  var DEPOSIT_PROP = '_spp2-deposit';
  var busy = false;     // one reconcile at a time per page
  var seenPlan = null;  // last plan id seen on this page

  function store(intent) {
    try { sessionStorage.setItem(KEY, JSON.stringify(intent)); } catch (e) {}
  }
  function take() {
    try {
      var raw = sessionStorage.getItem(KEY);
      if (!raw) return null;
      var intent = JSON.parse(raw);
      if (!intent || Date.now() - intent.ts > MAX_AGE) { sessionStorage.removeItem(KEY); return null; }
      return intent;
    } catch (e) { return null; }
  }
  function clear() {
    try { sessionStorage.removeItem(KEY); } catch (e) {}
  }

  function post(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    });
  }

  function depositChosen(conf) {
    var radio = document.querySelector('input[name="payment-option"]:checked');
    if (radio) return !/full/i.test(radio.value);
    // Spurit strips its radios from the DOM once an add lands. Treating "no radio"
    // as pay-in-full left the shopper on the product page with the deposit box
    // gone and no payment choice showing, seconds after choosing it. The
    // configurator keeps the choice in payMode — mirror its own depositSelected().
    return !!(conf && conf.payMode === 'deposit');
  }

  // The configurator only re-reads the quantity on `change`, so a typed number
  // is still sitting in the field unread. Force the sync before reading intent.
  function syncQuantity(conf) {
    var field = conf.querySelector('[data-poc-qty]');
    if (!field) return;
    if (document.activeElement === field && field.blur) field.blur();
    field.dispatchEvent(new Event('change', { bubbles: true }));
  }

  function readIntent(conf) {
    var form = conf.form || conf.querySelector('form[action*="/cart/add"]');
    var variant = form && form.querySelector('[data-poc-variant-id]');
    var id = variant && parseInt(variant.value, 10);
    if (!id) return null;

    var field = conf.querySelector('[data-poc-qty]');
    var qty = field ? parseInt(field.value, 10) : 1;
    if (!(qty > 0)) qty = 1;

    var addons = [];
    conf.querySelectorAll('input[data-poc-addon]:checked').forEach(function (input) {
      var addonId = parseInt(input.getAttribute('data-poc-addon'), 10);
      if (!addonId) return;
      var row = input.closest('.poc-addon');
      var own = row && row.querySelector('[data-poc-addon-qty]');
      var perBed = input.hasAttribute('data-poc-per-bed');
      var want = perBed ? qty : (own ? parseInt(own.value, 10) : 1);
      addons.push({ id: addonId, quantity: want > 0 ? want : 1 });
    });

    // The plan id is read now, not during the reconcile: Spurit strips its
    // injected inputs (and the payment radios) from the form once an add lands,
    // and after a redirect to /cart the product form is gone altogether.
    return { id: id, quantity: qty, addons: addons, deposit: depositChosen(conf), plan: notePlan(conf), ts: Date.now() };
  }

  function lineFor(cart, id) {
    for (var i = 0; i < cart.items.length; i++) if (cart.items[i].id === id) return cart.items[i];
    return null;
  }

  // location.reload() is served from the browser cache on the cart: the server
  // renders the repaired cart correctly while the shopper keeps seeing the
  // pre-repair one — three beds quoted at one bed's deposit, standard dates on a
  // rushed order. A unique query string forces a real fetch; start() tidies it
  // back out of the address bar.
  function freshReload() {
    try {
      location.replace(location.pathname + '?po-r=' + Date.now());
    } catch (e) {
      location.reload();
    }
  }

  // Rush quantity is derived from the bed count and deliberately not editable in
  // the cart, so Shopify's raw "no valid id or line parameter" is both alarming
  // and meaningless to a shopper. Say what the rule actually is.
  var RAW_ERR = /no valid id or line parameter/i;
  var RUSH_MSG = 'Rush orders cannot be edited in cart and are synced to bed count. Edit bed count to adjust.';

  function friendlyErrors() {
    if (!document.body) return;
    var walker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT, null);
    var node, hits = [];
    while ((node = walker.nextNode())) {
      if (RAW_ERR.test(node.nodeValue)) hits.push(node);
    }
    hits.forEach(function (t) { t.nodeValue = RUSH_MSG; });
  }

  function watchErrors() {
    if (!document.body || !window.MutationObserver) return;
    var pending = null;
    new MutationObserver(function () {
      if (pending) return;
      pending = setTimeout(function () { pending = null; friendlyErrors(); }, 200);
    }).observe(document.body, { childList: true, subtree: true, characterData: true });
    friendlyErrors();
  }

  // What the deposit sentence should say for a given cart — the same arithmetic as
  // snippets/po-cart-savings.liquid, in cents.
  function expectedToday(cart) {
    var cents = 0;
    cart.items.forEach(function (item) {
      if (item.selling_plan_allocation) {
        cents += Math.floor(item.original_line_price / 2);
      } else {
        // NOT discount_allocations: that array is populated in Liquid but comes back
        // empty from /cart.js, so reading it here scored every discounted line at
        // full price and would have fired a pointless reload on every bundle cart.
        // total_discount carries the allocated amount in the JSON.
        cents += item.original_line_price - (item.total_discount || 0);
      }
    });
    return cents / 100;
  }

  function renderedToday() {
    var m = (document.body ? document.body.innerText || '' : '').match(/Pay\s*\$([\d,]+(?:\.\d{2})?)\s*today/i);
    return m ? parseFloat(m[1].replace(/,/g, '')) : null;
  }

  // Ordering alone could not be trusted: Spurit's redirect gets /cart served in the
  // gap between the quantity change and the add-on add, so the page arrives quoting
  // a cart that no longer exists. Rather than guess when that happened, compare the
  // figure on the page against the live cart and refresh only if they disagree.
  // Guarded to once per arrival, so a genuine mismatch cannot loop.
  function verifyCartRender() {
    if (location.pathname.indexOf('/cart') !== 0) return;
    var shown = renderedToday();
    if (shown === null) return; // no deposit line rendered; nothing to check
    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        if (Math.abs(expectedToday(cart) - shown) <= 1) return; // agrees, within rounding
        var once = 0;
        try { once = parseInt(sessionStorage.getItem(STALE), 10) || 0; } catch (e) {}
        if (once === -1) return; // already refreshed once for this arrival
        try { sessionStorage.setItem(STALE, '-1'); } catch (e) {}
        freshReload();
      })
      .catch(function () {});
  }

  // One refresh, only on the cart, only just after a repair. The flag is cleared
  // before reloading, so this cannot loop.
  function refreshStaleCart() {
    var at = 0;
    try { at = parseInt(sessionStorage.getItem(STALE), 10) || 0; } catch (e) { return false; }
    if (at === -1) { try { sessionStorage.removeItem(STALE); } catch (e) {} return false; }
    try { sessionStorage.removeItem(STALE); } catch (e) {}
    if (!at || location.pathname.indexOf('/cart') !== 0) return false;
    if (Date.now() - at > 60000) return false;
    freshReload();
    return true;
  }

  function repair() {
    if (busy) return;
    var intent = take();
    if (!intent) return;
    busy = true;

    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var bed = lineFor(cart, intent.id);
        if (!bed) { busy = false; return; } // nothing was added — leave the cart alone

        var steps = [];

        // Quantity is only ever raised, so a shopper's own edit is never undone.
        if (bed.quantity < intent.quantity) {
          steps.push(function () { return post('/cart/change.js', { id: bed.key, quantity: intent.quantity }); });
        }

        // Restore the deposit plan if the add landed without it.
        if (intent.deposit && !bed.selling_plan_allocation) {
          var plan = intent.plan || seenPlan || bedPlan();
          if (plan) {
            steps.push(function () {
              return post('/cart/change.js', {
                id: bed.key,
                quantity: Math.max(bed.quantity, intent.quantity),
                selling_plan: plan
              });
            });
          }
        }

        intent.addons.forEach(function (addon) {
          var line = lineFor(cart, addon.id);
          if (!line) {
            steps.push(function () {
              var props = {};
              if (intent.deposit) props[DEPOSIT_PROP] = '1';
              return post('/cart/add.js', { items: [{ id: addon.id, quantity: addon.quantity, properties: props }] });
            });
          } else if (line.quantity < addon.quantity) {
            steps.push(function () { return post('/cart/change.js', { id: line.key, quantity: addon.quantity }); });
          }
        });

        if (!steps.length) { clear(); busy = false; verifyCartRender(); return; }

        // The cart API does not take concurrent writes; run them in order. The
        // intent is only cleared once the whole chain lands: Spurit redirects to
        // /cart mid-flight, and clearing early left the Rush line unadded with
        // nothing to retry from. Every step only raises a quantity or adds a
        // missing line, so finishing the job after the redirect is safe.
        return steps.reduce(function (chain, step) {
          // One failing step must not strand the rest. Attaching the plan 422s on
          // a variant Spurit has not provisioned yet ("Cannot apply selling plan
          // to variant"), and if that rejection killed the chain the Rush line
          // would never be added — trading a missing deposit for a missing bed.
          return chain.then(function () {
            return step().catch(function (err) {
              if (window.console && window.console.warn) {
                window.console.warn('[po-atc-guard] step failed, continuing', err);
              }
            });
          });
        }, Promise.resolve()).then(function () {
          clear();
          busy = false;
          // The cart page renders its ship window server-side from the cart as it
          // stood at page load — i.e. the one bed with no Rush. Repairing the cart
          // does not repaint it, so it would still quote the standard window while
          // the PDP quoted the rushed one. Re-render it. The intent is already
          // cleared, so this cannot loop.
          if (location.pathname.indexOf('/cart') === 0) { freshReload(); return; }
          // Spurit redirects to /cart the moment it adds, which can request the
          // page before these writes land — the cart then renders the pre-repair
          // cart, quoting one bed's deposit against a two-bed cart. Mark it so the
          // arriving page refreshes itself once.
          try { sessionStorage.setItem(STALE, String(Date.now())); } catch (e) {}
          document.documentElement.dispatchEvent(
            new CustomEvent('cart:refresh', { bubbles: true, detail: { source: 'po-atc-guard' } })
          );
        });
      })
      .catch(function (err) {
        busy = false;
        if (window.console && window.console.error) window.console.error('[po-atc-guard]', err);
      });
  }

  // The configurator's own variant data carries the deposit plan id, as
  // current.planId, from first render. Prefer it over Spurit's injected input:
  // on plushandoak.ca Spurit never writes that input at all, so the plan could not
  // be restored there and every deposit the configurator won was charged in full.
  // Reading it from the variant also removes the timing race on .com.
  function bedPlan(conf) {
    var el = conf || document.querySelector('po-configurator');
    if (el && el.current && el.current.planId > 0) return el.current.planId;
    var input = document.querySelector('form[action*="/cart/add"] input[name="selling_plan"]');
    var id = input && parseInt(input.value, 10);
    return id > 0 ? id : null;
  }

  // Cache whatever we find: Spurit writes its input asynchronously after the radio
  // changes and strips it again on add, so a shopper clicking straight through can
  // otherwise beat it there.
  function notePlan(conf) {
    var id = bedPlan(conf);
    if (id) seenPlan = id;
    return seenPlan;
  }

  // Sampling on a timer missed it: Spurit writes the input some time after the
  // radio changes, and the add then strips it again, so the whole window can fall
  // between two samples. Watch the form and catch it the instant it appears.
  function watchForPlan() {
    notePlan();
    var form = document.querySelector('form[action*="/cart/add"]');
    if (!form || !window.MutationObserver) return;
    new MutationObserver(notePlan).observe(form, {
      childList: true, subtree: true, attributes: true, attributeFilter: ['value']
    });
  }

  document.addEventListener('change', function (e) {
    var el = e.target;
    if (!el || !el.name || el.name !== 'payment-option') return;
    [0, 150, 400, 900, 1800, 3000].forEach(function (ms) { setTimeout(notePlan, ms); });
  }, true);

  window.addEventListener('click', function (e) {
    var btn = e.target && e.target.closest ? e.target.closest('[data-poc-atc]') : null;
    if (!btn || btn.disabled) return;
    var conf = btn.closest('po-configurator');
    if (!conf || conf.dataset.pocReady !== '1') return;

    syncQuantity(conf);
    var intent = readIntent(conf);
    if (!intent) return;
    store(intent);

    // Whoever handles the click, reconcile once it has settled. If Spurit
    // redirects to /cart instead, the load handler below picks the intent up.
    setTimeout(repair, 1200);
    setTimeout(repair, 3000);
  }, true);

  function start() {
    // Drop the cache-busting parameter so the shopper never sees it.
    if (/[?&]po-r=/.test(location.search)) {
      try { history.replaceState(null, '', location.pathname); } catch (e) {}
    }
    if (refreshStaleCart()) return; // reloading; nothing else is worth doing
    watchErrors();
    watchForPlan();
    repair();
    // After any repair has had time to land, make sure the page agrees with it.
    setTimeout(verifyCartRender, 2500);
    setTimeout(verifyCartRender, 6000);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();